// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  AsyncPipe,
  Location,
  NgFor,
  NgIf,
  NgTemplateOutlet,
} from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, map, tap } from 'rxjs';
import { SanitizeResourceUrlPipe } from 'src/app/modules/shared/pipes/sanitizeResourceUrl.pipe';
import { ClipboardService } from 'src/app/modules/shared/services/clipboard.service';
import { OperationalState } from '../../../../_interfaces/service-ticket-model';
import { APICallGateComponent } from '../../../shared/components/api-call-gate.component';
import { LocalizedDatePipe } from '../../../shared/pipes/localizedDate.pipe';
import { ScreenLayoutService } from '../../../shared/services/screen-layout-service/screen-layout.service';
import { SettingsService } from '../../../shared/services/settings.service';
import { DeepReadonly } from '../../../shared/utils/api-call-state.util';
import {
  DateFormatHelper,
  ShuiDatepickerDirective,
} from '../../../shared/utils/shuiDatepicker.directive';
import { RouteService } from '../../services/routeService.service';
import {
  ServiceTicketsService,
  TicketModelFIXED,
} from '../../services/service-tickets.service';
import { BackToSystemOverviewComponent } from '../backToSystemOverview/backToSystemOverview.component';
import { statusColor } from '../service-tickets-panel/service-tickets-panel.component';
import { isServiceTicket } from './service-tickets.shared';

type TSortKey =
  | 'creationDate'
  | 'ticketNumber'
  | 'description'
  | 'operationalState'
  | 'createdBy';

const defaultFilters = {
  searchString: '',
  operationalState: OperationalState.All,
  sortKey: 'creationDate' as TSortKey,
  sortDescending: true,
  daterange: [
    new Date(new Date().setDate(new Date().getDate() - 365)),
    new Date(),
  ],
};

@Component({
  selector: 'app-service-tickets-page',
  templateUrl: './service-tickets-page.component.html',
  styleUrls: [
    './service-tickets-page.component.scss',
    './service-tickets-shared.scss',
  ],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BackToSystemOverviewComponent,
    APICallGateComponent,
    NgTemplateOutlet,
    NgIf,
    ShuiDatepickerDirective,
    NgFor,
    LocalizedDatePipe,
    AsyncPipe,
    TranslateModule,
    SanitizeResourceUrlPipe,
  ],
})
export class ServiceTicketsPageComponent {
  // transient state
  filters$ = new BehaviorSubject(defaultFilters);
  dateBounds$ = new BehaviorSubject({
    minDate: 0,
    maxDate: 0,
  });

  dateBoundsForShui$ = this.dateBounds$.pipe(
    map(({ minDate, maxDate }) => ({
      min: DateFormatHelper.getFormattedDate(
        new Date(minDate),
        'YYYYMMDD',
        '-',
        'en',
      ),
      max: DateFormatHelper.getFormattedDate(
        new Date(maxDate),
        'YYYYMMDD',
        '-',
        'en',
      ),
    })),
  );

  get dateRangeValid(): boolean {
    return (
      !!this.dateBounds$.getValue().minDate &&
      !!this.dateBounds$.getValue().maxDate
    );
  }

  protected allTickets$ =
    this.serviceTicketsService.getTickets.lastSuccessfulData$.pipe(
      // filter for planned activities if needed
      map((tickets) => {
        return tickets.filter(isServiceTicket);
      }),
    );

  // list of tickets with added status color
  tickets$ = this.allTickets$.pipe(
    // extract min/max date
    tap((tickets) => {
      let minDate = Infinity;
      let maxDate = 0;
      tickets.forEach(({ creationDate }) => {
        const date = new Date(creationDate || new Date()).getTime();
        minDate = Math.min(minDate, date);
        maxDate = Math.max(maxDate, date);
      });
      const newDateBounds = {
        minDate,
        maxDate,
      };
      if (
        newDateBounds.maxDate != this.dateBounds$.value.maxDate ||
        newDateBounds.minDate != this.dateBounds$.value.minDate
      ) {
        this.dateBounds$.next(newDateBounds);
        // set initial DateRange
        const daterange = [new Date(minDate), new Date(maxDate)];
        this.updateFilters({
          daterange,
        });
      }
    }),
  );

  // total ticket count
  totalTicketsCount$ = this.tickets$.pipe(map((tickets) => tickets.length));

  // all operational states from current data
  operationalStates$ = this.tickets$.pipe(
    map((tickets) => [
      OperationalState.All,
      ...Array.from(new Set(tickets.map((ticket) => ticket.operationalState))),
    ]),
  );

  // filtered and sorted
  processedTickets$ = combineLatest({
    tickets: this.tickets$,
    dateBounds: this.dateBounds$,
    filters: this.filters$,
  }).pipe(
    // filter by operation
    map(({ tickets, filters }) => {
      if (filters.operationalState != OperationalState.All) {
        tickets = tickets.filter(
          (ticket) => ticket.operationalState == filters.operationalState,
        );
      }
      return { filters, tickets };
    }),
    // filter by searchString
    map(({ tickets, filters }) => {
      if (filters.searchString) {
        const searchText = filters.searchString.toLowerCase();
        tickets = tickets.filter(
          (ticket) =>
            ticket.ticketNumber.toLowerCase().includes(searchText) ||
            ticket.description?.toLowerCase().includes(searchText) ||
            ticket.operationalState.toLowerCase().includes(searchText) ||
            ticket.createdBy?.toLowerCase().includes(searchText),
        );
      }
      return { filters, tickets };
    }),
    // filter by date range
    map(({ tickets, filters }) => {
      const start = filters.daterange[0].getTime();
      const end = new Date(
        new Date(filters.daterange[1]).setHours(23, 59, 59, 999),
      ).getTime();

      tickets = tickets.filter(
        (ticket) =>
          new Date(ticket.creationDate).getTime() >= start &&
          new Date(ticket.creationDate).getTime() <= end,
      );
      return { tickets, filters };
    }),
    map(({ tickets, filters }) => {
      const ticketsWithColor = tickets.map((ticket) => ({
        ...ticket,
        statusColor: statusColor(ticket.operationalState),
      }));
      return {
        tickets: ticketsWithColor,
        filters,
      };
    }),
    // sort and unpack tickets
    map(({ tickets, filters }) =>
      [...tickets].sort(
        ServiceTicketsPageComponent.sortFunction(
          filters.sortKey,
          this.filters$.getValue().sortDescending,
        ),
      ),
    ),
  );

  // filteredTicket count
  processedTicketsCount$ = this.processedTickets$.pipe(
    map((tickets) => tickets.length),
  );

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public serviceTicketsService: ServiceTicketsService,
    public location: Location,
    public screenLayoutService: ScreenLayoutService,
    protected routeService: RouteService,
    protected clipboard: ClipboardService,
    protected settingsService: SettingsService,
  ) {
    routeService.currentSystem$
      .pipe(takeUntilDestroyed())
      .subscribe((system) => {
        if (!system) {
          return;
        }

        const { systemMaterialNumber, systemSerialNumber } = system;

        this.serviceTicketsService.getTickets.request(
          {
            systemMaterialNumber,
            systemSerialNumber,
          },
          {
            // request might have been made on parent page already
            // we can reuse the results in case there are any!
            clearStaleData: false,
          },
        );
      });

    // Subscribe to settings changes to update date bounds and filters when date format changes
    this.settingsService.settings$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.updateDateBoundsAndFiltersOnSettingsChange();
    });
  }

  // Called when settings change to update date bounds and filters
  private updateDateBoundsAndFiltersOnSettingsChange(): void {
    // Get the current filters
    const currentFilters = this.filters$.getValue();
    // Recalculate only the date-related fields using the current date bounds
    const minDate = this.dateBounds$.getValue().minDate;
    const maxDate = this.dateBounds$.getValue().maxDate;
    const daterange = [new Date(minDate), new Date(maxDate)];
    const mergedFilters = {
      ...currentFilters,
      daterange,
    };
    this.filters$.next(mergedFilters);
    this.dateBounds$.next({
      minDate,
      maxDate,
    });
  }

  updateFilters(filters: Partial<typeof defaultFilters>): void {
    if (filters.daterange && filters.daterange.length !== 2) {
      filters.daterange = defaultFilters.daterange;
    }
    this.filters$.next({
      ...this.filters$.getValue(),
      ...filters,
    });
  }

  clearAndResetDateRange(): void {
    this.updateFilters({
      daterange: [
        new Date(this.dateBounds$.getValue().minDate),
        new Date(this.dateBounds$.getValue().maxDate),
      ],
    });
  }

  onDateRangeChanged(daterange: [Date, Date]): void {
    this.updateFilters({
      daterange,
    });
  }

  onChangeSort(sortKey: TSortKey, sortDescending: boolean): void {
    this.updateFilters({
      sortDescending,
      sortKey,
    });
  }

  onOpertationalStateChanged(operationalState: OperationalState): void {
    this.updateFilters({
      operationalState,
    });
  }

  onSearchString(searchString: string): void {
    this.updateFilters({
      searchString,
    });
  }

  resetFilters(): void {
    this.updateFilters({
      searchString: '',
      operationalState: OperationalState.All,
      sortDescending: true,
      sortKey: 'creationDate',
    });
    this.clearAndResetDateRange();
  }

  protected static sortFunction(
    sortKey: TSortKey,
    descending: boolean,
  ): (
    a: DeepReadonly<TicketModelFIXED>,
    b: DeepReadonly<TicketModelFIXED>,
  ) => number {
    const sort = ServiceTicketsPageComponent.sortFunctions[sortKey];
    return (
      a: DeepReadonly<TicketModelFIXED>,
      b: DeepReadonly<TicketModelFIXED>,
    ) => {
      return sort(a, b) * (descending ? 1 : -1);
    };
  }

  protected static sortFunctions: {
    [k in TSortKey]: (
      a: DeepReadonly<TicketModelFIXED>,
      b: DeepReadonly<TicketModelFIXED>,
    ) => number;
  } = {
    creationDate: ServiceTicketsPageComponent.sortByDate,
    ticketNumber: (a, b) => b.ticketNumber.localeCompare(a.ticketNumber),
    createdBy: (a, b) => (b.createdBy || '').localeCompare(a.createdBy || ''),
    description: (a, b) =>
      (b.description || '').localeCompare(a.description || ''),
    operationalState: (a, b) =>
      (b.operationalState as unknown as string).localeCompare(
        a.operationalState as unknown as string,
      ),
  };

  private static sortByDate(a: TicketModelFIXED, b: TicketModelFIXED): number {
    return (
      (b.creationDate ? Date.parse(b.creationDate.toString()) : 0) -
      (a.creationDate ? Date.parse(a.creationDate.toString()) : 0)
    );
  }

  isOverflowing(el: HTMLElement): boolean {
    if (!el.style.overflow || el.style.overflow === 'visible') {
      el.style.overflow = 'hidden';
    }

    return el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
  }

  backToOverview(): void {
    this.location.back();
  }
}
